import { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Camera, Home as HomeIcon, BookOpen, UserCircle2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1600";

export default function SecondLife() {
  const [characters, setCharacters] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [journals, setJournals] = useState([]);

  useEffect(() => {
    api.get("/characters").then(r => setCharacters(r.data)).catch(() => {});
    api.get("/gallery", { params: { category: "second-life" } }).then(r => setPhotos(r.data)).catch(() => {});
    api.get("/posts", { params: { category: "second-life" } }).then(r => setJournals(r.data)).catch(() => {});
  }, []);

  const primary = characters.filter(c => c.is_primary);
  const supporting = characters.filter(c => !c.is_primary);

  return (
    <div className="page-container fade-in" data-testid="second-life-page">
      <div className="parchment-card relative overflow-hidden p-8 md:p-12 mb-10">
        <img src={HERO} alt="mossy hollow" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative">
          <div className="page-eyebrow">virtual worlds</div>
          <h1 className="page-title">Second Life Adventures</h1>
          <p className="page-sub mt-3">Sim explorations, slow-roleplay archives, character notebooks, and a lot of photographs of light through trees.</p>
        </div>
      </div>

      {/* CHARACTERS */}
      <h2 className="section-title flex items-center gap-2"><Sparkles className="h-5 w-5" style={{ color: "var(--gold)" }} /> Character Profiles</h2>

      {primary.length > 0 && (
        <div className="mt-5 grid gap-5">
          {primary.map(c => (
            <article key={c.id} className="parchment-card p-6 md:p-8 grid md:grid-cols-[260px_1fr] gap-6 items-start" data-testid={`character-primary-${c.id}`}>
              {c.image_url ? (
                <img src={c.image_url} alt={c.name} className="w-full h-64 md:h-80 object-cover rounded-sm" style={{ border: "1px solid rgba(212,175,55,0.25)" }} />
              ) : (
                <div className="w-full h-64 md:h-80 grid place-items-center rounded-sm" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(212,175,55,0.18)" }}>
                  <UserCircle2 className="h-20 w-20" style={{ color: "rgba(212,175,55,0.4)" }} />
                </div>
              )}
              <div>
                <div className="page-eyebrow">{c.role || "primary character"}</div>
                <h3 className="tavern-heading text-3xl md:text-4xl gold-text mt-1">{c.name}</h3>
                <p className="mt-4 leading-relaxed whitespace-pre-line" style={{ color: "var(--silver)" }}>
                  {c.description || "A portrait is on the way."}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}

      {supporting.length > 0 && (
        <>
          <h3 className="section-title mt-10 text-xl flex items-center gap-2" style={{ color: "var(--orchid-light)" }}>
            <BookOpen className="h-4 w-4" /> Supporting Cast
          </h3>
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            {supporting.map(c => (
              <div key={c.id} className="parchment-card p-5 flex gap-4" data-testid={`character-supporting-${c.id}`}>
                {c.image_url ? (
                  <img src={c.image_url} alt={c.name} className="w-28 h-28 object-cover rounded-sm flex-shrink-0" />
                ) : (
                  <div className="w-28 h-28 grid place-items-center rounded-sm flex-shrink-0" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(212,175,55,0.18)" }}>
                    <UserCircle2 className="h-12 w-12" style={{ color: "rgba(212,175,55,0.4)" }} />
                  </div>
                )}
                <div>
                  <div className="page-eyebrow">{c.role || "character"}</div>
                  <div className="tavern-heading text-2xl gold-text mt-1">{c.name}</div>
                  <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--muted)" }}>{c.description}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {characters.length === 0 && (
        <div className="parchment-card p-6 mt-5 text-sm" style={{ color: "var(--muted)" }} data-testid="characters-empty">
          The character notebook is waiting for its first entry. Add one from the Keeper's Quarters.
        </div>
      )}

      {/* PHOTOGRAPHY */}
      <h2 className="section-title flex items-center gap-2 mt-12"><Camera className="h-5 w-5" style={{ color: "var(--gold)" }} /> Photography</h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mt-5">
        {photos.map(p => (
          <div key={p.id} className="room-card overflow-hidden" data-testid={`sl-photo-${p.id}`}>
            <img src={p.image_url} alt={p.title} className="w-full aspect-square object-cover" />
            <div className="p-3">
              <div className="tavern-heading text-base gold-text">{p.title}</div>
              {p.caption && <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{p.caption}</div>}
            </div>
          </div>
        ))}
        {!photos.length && (
          <div className="col-span-full text-sm" style={{ color: "var(--muted)" }}>Photographs being developed in the dark room.</div>
        )}
      </div>

      {/* JOURNALS */}
      <h2 className="section-title flex items-center gap-2 mt-12"><BookOpen className="h-5 w-5" style={{ color: "var(--gold)" }} /> Exploration Journals</h2>
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        {journals.map(j => (
          <div key={j.id} className="parchment-card p-5" data-testid={`sl-journal-${j.id}`}>
            <div className="tavern-heading text-xl gold-text">{j.title}</div>
            <div className="tavern-script text-lg mt-1" style={{ color: "var(--orchid-light)" }}>{j.excerpt}</div>
            <p className="text-sm mt-3 whitespace-pre-line" style={{ color: "var(--muted)" }}>{j.body}</p>
          </div>
        ))}
      </div>

      <div className="parchment-card p-6 md:p-8 mt-12" data-testid="decorating-note">
        <h2 className="section-title flex items-center gap-2"><HomeIcon className="h-5 w-5" style={{ color: "var(--gold)" }} /> Home Decorating Projects</h2>
        <p className="text-sm mt-3" style={{ color: "var(--muted)" }}>
          The tavern itself is a long-running build: candlelit alcoves, antique books stacked against ivy walls,
          a tiny observatory in the loft. Decorating posts and before/after shots live alongside the Gallery as they're finished.
        </p>
      </div>
    </div>
  );
}
